import React from 'react';
import { Spinner } from '@/components/ui/spinner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const MAX_CAMPAIGN_BRIEF_WORDS = 800;

const countWords = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
};

export default function CampaignBriefSection({
  campaignBrief,
  onCampaignBriefChange,
  campaignOptions,
  topSlot = null,
  notesRows = 4,
  notesPlaceholder = 'Add any details or creative direction you want. Leave it blank and AI will decide what works best for your business.',
}) {
  const updateBrief = (updates) => {
    onCampaignBriefChange?.({ ...campaignBrief, ...updates });
  };

  const updateOffer = (updates) => {
    const currentOffer = campaignBrief?.offers?.[0] || {};
    updateBrief({ offers: [{ ...currentOffer, ...updates }] });
  };

  if (!campaignOptions) {
    return <div className="flex justify-center py-8"><Spinner /></div>;
  }

  return (
    <div className="grid gap-5">
      {topSlot}

      <div className="space-y-2">
        <Label>Campaign Goal</Label>
        <Select
          value={campaignBrief?.purpose || 'awareness'}
          onValueChange={(value) => updateBrief({ purpose: value, seasonal_theme: null, offers: [] })}
        >
          <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            {campaignOptions.purpose?.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {campaignBrief?.purpose === 'seasonal' ? (
        <div className="animate-in slide-in-from-top-2 fade-in space-y-2">
          <Label>Seasonal Theme <span className="text-destructive">*</span></Label>
          <Select
            value={campaignBrief?.seasonal_theme || ''}
            onValueChange={(value) => updateBrief({ seasonal_theme: value || null })}
          >
            <SelectTrigger className="h-11 border-coral/50"><SelectValue placeholder="Select theme..." /></SelectTrigger>
            <SelectContent>
              {campaignOptions.seasonal_theme?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {campaignBrief?.purpose === 'promotion' ? (
        <div className="animate-in slide-in-from-top-2 fade-in space-y-4 rounded-xl border bg-muted/20 p-4">
          <div className="space-y-2">
            <Label>Headline <span className="text-destructive">*</span></Label>
            <Input
              className="h-10"
              placeholder="e.g., 20% Off Your First Order"
              value={campaignBrief?.offers?.[0]?.headline || ''}
              onChange={(event) => updateOffer({ headline: event.target.value })}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Promo Code</Label>
              <Input
                className="h-10"
                placeholder="e.g., WELCOME20"
                value={campaignBrief?.offers?.[0]?.code || ''}
                onChange={(event) => updateOffer({ code: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Valid Until</Label>
              <Input
                className="h-10"
                type="date"
                value={campaignBrief?.offers?.[0]?.valid_until || ''}
                onChange={(event) => updateOffer({ valid_until: event.target.value || undefined })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Fine Print</Label>
            <Input
              className="h-10"
              placeholder="e.g., Min $25 order. Dine-in only."
              value={campaignBrief?.offers?.[0]?.fine_print || ''}
              onChange={(event) => updateOffer({ fine_print: event.target.value })}
            />
          </div>
        </div>
      ) : null}

      {/* "Channel" is hidden for the MVP (the brief "Tone" below is kept). */}
      <div className="space-y-2">
        <Label>Tone</Label>
        <Select
          value={campaignBrief?.tone || 'auto'}
          onValueChange={(value) => updateBrief({ tone: value })}
        >
          <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            {campaignOptions.tone?.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between">
          <Label>Personalize your video</Label>
          <span className="text-xs text-muted-foreground">
            {countWords(campaignBrief?.notes || '')}/{MAX_CAMPAIGN_BRIEF_WORDS} words
          </span>
        </div>
        <Textarea
          rows={notesRows}
          className="resize-none"
          placeholder={notesPlaceholder}
          value={campaignBrief?.notes || ''}
          onChange={(event) => {
            if (countWords(event.target.value) <= MAX_CAMPAIGN_BRIEF_WORDS) {
              updateBrief({ notes: event.target.value });
            }
          }}
        />
      </div>
    </div>
  );
}
